import React, { useState, useEffect } from 'react';
import { Route, Switch, Redirect } from 'react-router-dom';
import { Routes } from '../routes';
import { useAuth } from '../context/AuthContext';
import { useAdminAuth } from '../context/AdminAuthContext';

import Welcome from './flowfence/Welcome';
import Login from './flowfence/Login';
import Register from './flowfence/Register';
import Dashboard from './flowfence/Dashboard';
import Decide from './flowfence/Decide';
import Rules from './flowfence/Rules';
import RuleDetail from './flowfence/RuleDetail';
import Blocklist from './flowfence/Blocklist';
import Tokens from './flowfence/Tokens';
import AccountSettings from './flowfence/AccountSettings';
import Developers from './flowfence/Developers';
import AdminLogin from './flowfence/AdminLogin';
import AdminTenants from './flowfence/AdminTenants';
import AdminTenantDetail from './flowfence/AdminTenantDetail';
import AdminOperators from './flowfence/AdminOperators';
import AdminAccount from './flowfence/AdminAccount';
import AdminAcceptInvite from './flowfence/AdminAcceptInvite';

import Sidebar from '../components/Sidebar';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Preloader from '../components/Preloader';
import ProtectedRoute from '../components/ProtectedRoute';
import AdminProtectedRoute from '../components/AdminProtectedRoute';

const RouteWithLoader = ({ component: Component, ...rest }) => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setLoaded(true), 400);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Route {...rest} render={(props) => (
      <>
        <Preloader show={!loaded} />
        <Component {...props} />
      </>
    )} />
  );
};

const RouteWithSidebar = ({ component: Component, ...rest }) => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setLoaded(true), 400);
    return () => clearTimeout(timer);
  }, []);

  const [showSettings, setShowSettings] = useState(
    localStorage.getItem('settingsVisible') !== 'false'
  );

  const toggleSettings = () => {
    const next = !showSettings;
    setShowSettings(next);
    localStorage.setItem('settingsVisible', String(next));
  };

  return (
    <ProtectedRoute
      {...rest}
      render={(props) => (
        <>
          <Preloader show={!loaded} />
          <Sidebar />
          <main className="content">
            <Navbar />
            <Component {...props} />
            <Footer toggleSettings={toggleSettings} showSettings={showSettings} />
          </main>
        </>
      )}
    />
  );
};

const AdminRouteWithLayout = ({ component: Component, ...rest }) => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setLoaded(true), 400);
    return () => clearTimeout(timer);
  }, []);

  return (
    <AdminProtectedRoute
      {...rest}
      render={(props) => (
        <>
          <Preloader show={!loaded} />
          <main className="content content-admin px-4 px-lg-5">
            <Component {...props} />
          </main>
        </>
      )}
    />
  );
};

function PublicOnlyRoute({ component: Component, ...rest }) {
  const { isAuthenticated } = useAuth();
  return (
    <Route
      {...rest}
      render={(props) =>
        isAuthenticated ? (
          <Redirect to={Routes.Dashboard.path} />
        ) : (
          <Component {...props} />
        )
      }
    />
  );
}

function AdminPublicOnlyRoute({ component: Component, ...rest }) {
  const { isAdminAuthenticated } = useAdminAuth();
  return (
    <Route
      {...rest}
      render={(props) =>
        isAdminAuthenticated ? (
          <Redirect to={Routes.AdminTenants.path} />
        ) : (
          <Component {...props} />
        )
      }
    />
  );
}

export default function HomePage() {
  return (
    <Switch>
      <RouteWithLoader exact path={Routes.Welcome.path} component={Welcome} />

      <PublicOnlyRoute exact path={Routes.Login.path} component={Login} />
      <PublicOnlyRoute exact path={Routes.Register.path} component={Register} />
      <RouteWithLoader exact path={Routes.Developers.path} component={Developers} />

      <AdminPublicOnlyRoute exact path={Routes.AdminLogin.path} component={AdminLogin} />
      <RouteWithLoader exact path={Routes.AdminAcceptInvite.path} component={AdminAcceptInvite} />
      <AdminRouteWithLayout exact path={Routes.AdminTenants.path} component={AdminTenants} />
      <AdminRouteWithLayout exact path={Routes.AdminTenantDetail.path} component={AdminTenantDetail} />
      <AdminRouteWithLayout exact path={Routes.AdminOperators.path} component={AdminOperators} />
      <AdminRouteWithLayout exact path={Routes.AdminAccount.path} component={AdminAccount} />

      <RouteWithSidebar exact path={Routes.Dashboard.path} component={Dashboard} />
      <RouteWithSidebar exact path={Routes.Decide.path} component={Decide} />
      <RouteWithSidebar exact path={Routes.Rules.path} component={Rules} />
      <RouteWithSidebar exact path={Routes.RuleDetail.path} component={RuleDetail} />
      <RouteWithSidebar exact path={Routes.Blocklist.path} component={Blocklist} />
      <RouteWithSidebar exact path={Routes.Tokens.path} component={Tokens} />
      <RouteWithSidebar exact path={Routes.Account.path} component={AccountSettings} />

      <Redirect to={Routes.Welcome.path} />
    </Switch>
  );
}
